import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../../components/Navbar';
import { deliveryServiceAPI } from '../../services/api';

/**
 * Admin: manage the restaurant-booked "Delivery Service" (courier-only). Enable/disable the
 * feature and set the km-range pricing slabs. Charge = actual road distance mapped to the
 * matching slab; a distance beyond the top slab uses the top slab's charge. Platform keeps
 * the full charge (rider + restaurant earn 0 on these orders).
 */
function DeliveryServiceSettings() {
  const [enabled, setEnabled] = useState(false);
  const [slabs, setSlabs] = useState([]); // [{ minKm, maxKm, chargeRupees }]
  const [perStop, setPerStop] = useState(15); // QuickWinc multi-stop: ₹ per extra drop
  const [loaded, setLoaded] = useState(false);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await deliveryServiceAPI.getConfig();
      const d = r.data?.data || {};
      setEnabled(!!d.enabled);
      setSlabs((d.slabs || []).map((s) => ({ minKm: s.minKm, maxKm: s.maxKm, chargeRupees: s.chargeRupees })));
      setPerStop(d.perStopChargeRupees ?? 15);
      setLoaded(true);
    } catch (e) { setMsg('Failed to load settings'); setLoaded(true); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setSlab = (i, k, v) => setSlabs((prev) => prev.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const addSlab = () => {
    const lastMax = slabs.length ? Number(slabs[slabs.length - 1].maxKm) || 0 : 0;
    setSlabs((prev) => [...prev, { minKm: lastMax, maxKm: lastMax + 3, chargeRupees: '' }]);
  };
  const removeSlab = (i) => setSlabs((prev) => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const clean = slabs
        .map((s) => ({ minKm: Number(s.minKm), maxKm: Number(s.maxKm), chargeRupees: Number(s.chargeRupees) }))
        .filter((s) => [s.minKm, s.maxKm, s.chargeRupees].every(Number.isFinite) && s.maxKm >= s.minKm && s.chargeRupees >= 0);
      await deliveryServiceAPI.updateConfig({ enabled, slabs: clean, perStopChargeRupees: Number(perStop) || 0 });
      setMsg('Saved — live.');
      load();
    } catch (e) { setMsg(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const card = { background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 4px 10px rgba(0,0,0,0.05)' };
  const input = { width: '100%', padding: '9px 11px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };

  if (!loaded) return (<div><Navbar /><div style={{ padding: 24 }}>Loading…</div></div>);

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6f8' }}>
      <Navbar />
      <div style={{ maxWidth: 720, margin: '0 auto', padding: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0F172A', margin: '0 0 4px' }}>🚚 Delivery Service</h1>
        <p style={{ color: '#64748B', fontSize: 14, margin: '0 0 18px' }}>
          Restaurants can book a courier-only delivery from their app (no food items). The platform earns the
          full delivery charge below. Charge = road distance from the restaurant to the drop, matched to a km slab
          (a distance beyond the last slab uses the last slab's charge).
        </p>

        <div style={card}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: '#334155' }}>
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            Enabled (restaurants can book delivery service)
          </label>
        </div>

        <div style={card}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>Pricing slabs (km range → charge)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 8, fontSize: 12, color: '#64748B', fontWeight: 600, marginBottom: 6 }}>
            <div>From (km)</div><div>To (km)</div><div>Charge (₹)</div><div></div>
          </div>
          {slabs.length === 0 && <div style={{ color: '#94a3b8', fontSize: 14, marginBottom: 10 }}>No slabs yet — add one below.</div>}
          {slabs.map((s, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <input style={input} type="number" value={s.minKm} onChange={(e) => setSlab(i, 'minKm', e.target.value)} />
              <input style={input} type="number" value={s.maxKm} onChange={(e) => setSlab(i, 'maxKm', e.target.value)} />
              <input style={input} type="number" value={s.chargeRupees} onChange={(e) => setSlab(i, 'chargeRupees', e.target.value)} />
              <button onClick={() => removeSlab(i)} style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: 8, padding: '8px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Remove</button>
            </div>
          ))}
          <button onClick={addSlab} style={{ marginTop: 6, background: '#EEF2FF', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>+ Add slab</button>
          <p style={{ color: '#94a3b8', fontSize: 12, marginTop: 12 }}>
            Example: 0–3 km ₹40 · 3–6 km ₹60 · 6–10 km ₹90 · 10–25 km ₹140. Keep ranges contiguous; overlaps
            resolve to the first matching slab (lowest From).
          </p>
        </div>

        <div style={card}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Multi-stop charge (QuickWinc)</div>
          <p style={{ color: '#64748B', fontSize: 13, margin: '0 0 10px' }}>
            Flat ₹ added per <b>extra</b> drop on a multi-stop booking (1 pickup → many drops).
            Total = slab(total route distance) + this × (drops − 1).
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 700 }}>₹</span>
            <input style={{ ...input, maxWidth: 140 }} type="number" min="0" value={perStop}
              onChange={(e) => setPerStop(e.target.value)} />
            <span style={{ color: '#64748B', fontSize: 13 }}>per extra stop</span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={save} disabled={saving} style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          {msg && <span style={{ fontSize: 14, color: msg.includes('Saved') ? '#16a34a' : '#dc2626' }}>{msg}</span>}
        </div>
      </div>
    </div>
  );
}

export default DeliveryServiceSettings;
